import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  MenuDragAndDrop,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { type ColumnState } from '../../../features/ViolationsTab/data-layer/violationsTabTypes';
import { useInvertedLocalize } from '../../hooks';

import './SodColumnsList.css';

interface SodColumnsListProps {
  columns: ColumnState[];
  isDisabled?: boolean;
  handleColumnsChange?: (columns: ColumnState[]) => void;
}

/**
 * SodColumnsList
 */
function SodColumnsList({
  columns,
  isDisabled,
  handleColumnsChange,
}: SodColumnsListProps): ReactElement {
  const l = useInvertedLocalize();
  const [options, setOptions] = useState(columns);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (handleColumnsChange) {
      handleColumnsChange(options);
    }
  }, [options, handleColumnsChange]);

  useEffect(() => {
    if (isDisabled) {
      setIsExpanded(false);
    }
  }, [isDisabled]);

  const countActiveColumns = useCallback(
    (menuColumns: ColumnState[]): number =>
      menuColumns.reduce(
        (count, option) => (option.isChecked ? count + 1 : count),
        0
      ),
    []
  );

  const currentCount = countActiveColumns(options);

  const renderedMenuTrigger = (
    <ButtonSelect
      kind='rectangle'
      color={currentCount ? 'neutral' : 'transparent'}
      label={l('List')}
      ariaLabel='List'
      size='small'
      isDisabled={isDisabled ?? options.length === 0}
      prefixIcon={<Icon kind='Edit' />}
      isVisualCueEnabled={false}
      BadgeComp={
        currentCount ? <Badge kind='dot' color='primary' size='small' /> : null
      }
    />
  );

  const detailsPanel = (
    <Tooltip
      trigger={
        <Typography kind='body3' color='--color-foreground-secondary-bold'>
          {l('details panel')}
        </Typography>
      }
      text={l('Tap anywhere on the row to open the details panel.')}
      placement='bottom-start'
    />
  );

  const updateColumns = useCallback(
    (menuColumns: ColumnState[], activeCount: number) =>
      menuColumns.map((item) => {
        if (activeCount >= 5 && !item.isChecked) {
          return { ...item, isDisabled: true };
        }

        if (activeCount < 5 && item.isDisabled) {
          return { ...item, isDisabled: false };
        }

        return item;
      }),
    []
  );

  const modifiedSetOptions = useCallback(
    (optionsToModify: ColumnState[]) => {
      const activeColumnCount = countActiveColumns(optionsToModify);

      const updatedColumns = updateColumns(optionsToModify, activeColumnCount);

      setOptions(updatedColumns);
    },
    [countActiveColumns, updateColumns]
  );

  const [columnsLoaded, setColumnsLoaded] = useState(false);

  useEffect(() => {
    if (columns.length > 0 && !columnsLoaded) {
      modifiedSetOptions(columns);
      setColumnsLoaded(true);
    }
  }, [columns, columnsLoaded, modifiedSetOptions]);

  const labelText = l(
    'The number of visible attributes depends on the size of your browser. Any attributes not shown are visible in the'
  );
  const maxColumnsSelected = l(
    `You've reached maximum number of columns sets allowed. Remove existing ones to add more.`
  );
  const label =
    countActiveColumns(options) >= 5 ? (
      <InlineMessage
        kind='background'
        text={maxColumnsSelected}
        colorTheme='info'
        size='medium'
        isVisible
        isDismissible={false}
        leftIcon={<Icon kind='AlertInfoFilled' />}
      />
    ) : (
      <span>
        {labelText} {detailsPanel}.
      </span>
    );

  return (
    <Box className='SodColumnsList'>
      <MenuDragAndDrop
        label={label}
        options={options}
        setOptions={modifiedSetOptions}
        trigger={renderedMenuTrigger}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        minOptionsForSearch={options.length + 1}
        onChange={() => {}}
      />
    </Box>
  );
}

SodColumnsList.defaultProps = {
  isDisabled: false,
  handleColumnsChange: () => {},
};

export default memo(SodColumnsList);
